import {
    useMutation,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { InitialisationAvailableT, InitialisationT } from './type';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useInitialisationQuery } from 'routes/BudgetModule/helpers/hooks/useCustomQuery';
import { useDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to fetch the initialisation data for a given initialisation ID.
 */
export const useGetInitialisation = (
    initialisationId: number,
): UseQueryResult<InitialisationT, HttpError> => {
    const { initialisationKeys } = createBudgetQueryKeys();
    return useInitialisationQuery(
        initialisationKeys({ initialisationId }),
        async () => {
            const response = await RQAPICallWithData<InitialisationT>({
                endpoint: `budget/admin/initialisations/${initialisationId}`,
                method: 'GET',
            });

            return response.content.data;
        },
    );
};

/**
 * Custom hook to check if initialisations are available.
 * Used to check if you can start one or not.
 */
export const useGetInitialisationAvailable = (): UseQueryResult<
    boolean,
    HttpError
> => {
    const { initialisationKeys } = createBudgetQueryKeys();
    return useInitialisationQuery(
        initialisationKeys({ isAvailable: true }),
        async () => {
            const response = await RQAPICallWithData<InitialisationAvailableT>({
                endpoint: `budget/admin/initialisations/status`,
                method: 'GET',
            });

            return response.content.data.available;
        },
    );
};

/**
 * Custom hook to delete an initialisation by its ID.
 */
export const useDeleteInitialisation = (initialisationId: number) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { initialisationKeys } = createBudgetQueryKeys();
    return useMutation({
        mutationFn: async () => {
            const response = await RQAPICall({
                endpoint: `budget/admin/initialisations/${initialisationId}`,
                method: 'DELETE',
            });
            return response.content.data;
        },

        onSuccess: () => {
            queryClient.setQueryData(
                initialisationKeys({ initialisationId }),
                (initialisation: InitialisationT) => {
                    if (initialisation) {
                        return {
                            ...initialisation,
                            deleted: true,
                        };
                    }
                    return initialisation;
                },
            );
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.deleteInitialisation.success'),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};
